import React, { useCallback, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Grid from '@material-ui/core/Grid';
import OrderPreview from './OrderPreview';
import DataGridPreview from '@afosto/app-components-react/DataGrid/DataGridPreview';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import OrdersGrid from '@afosto/app-components-react/OrdersGrid';
import Page from '@afosto/app-components-react/Page';
import { orders as ordersApi } from '@afosto/order/api/Orders';
import translations from './translations';

const Orders = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:orders:read']);

  const intl = useIntl();
  const confirm = useConfirm();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [isFetchingOrder, setIsFetchingOrder] = useState(false);
  const [order, setOrder] = useState(null);
  const [activeItem, setActiveItem] = useState(null);

  /**
   * Fetch an order by ID.
   * @param {string} id
   * @returns {Promise}
   */
  const fetchOrder = useCallback(async id => {
    setIsFetchingOrder(true);

    try {
      const response = await ordersApi.getOrder(id);
      const { data = {} } = response;

      setOrder(data);
    } catch (error) {
      setOrder(null);
    } finally {
      setIsFetchingOrder(false);
    }
  }, []);

  const handleActiveItem = useCallback(
    activeRow => {
      setActiveItem(activeRow);

      if (activeRow) {
        fetchOrder(activeRow);
      } else {
        setOrder(null);
      }
    },
    [fetchOrder],
  );

  /**
   * Handle delete order.
   * @returns {Promise<void>}
   */
  const handleDeleteOrder = async () => {
    try {
      const confirmResponse = await confirm({
        title: intl.formatMessage(translations.confirmDeleteOrder.title),
        description: intl.formatMessage(translations.confirmDeleteOrder.description),
        catchOnCancel: false,
        buttons: [
          {
            label: intl.formatMessage(translations.confirmDeleteOrder.buttons.cancel),
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage(translations.confirmDeleteOrder.buttons.delete),
            response: 'deleteOrder',
            type: 'submit',
            variant: 'contained',
            color: 'primary',
          },
        ],
      });

      if (confirmResponse !== 'deleteOrder') {
        return;
      }

      await ordersApi.deleteOrder(order.id);

      history.push('/orders');

      enqueueSnackbar(intl.formatMessage(translations.feedback.orderDeleted), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.orderNotDeleted), {
        variant: 'error',
      });
    }
  };

  const previewMenu = [
    {
      key: 'view_order',
      label: intl.formatMessage(translations.previewActions.view.label),
      icon: 'eye',
      shortcut: true,
      onClick: () => history.push(`/orders/${order.id}`),
    },
    ...(!order?.state?.is_confirmed && hasPermissions(['odr:orders:write'])
      ? [
          {
            key: 'edit_order',
            label: intl.formatMessage(translations.previewActions.edit.label),
            icon: 'pencil',
            shortcut: true,
            onClick: () => history.push(`/orders/edit/${order.id}`),
          },
          {
            key: 'delete_order',
            label: intl.formatMessage(translations.previewActions.delete.label),
            icon: 'trash-alt',
            color: 'secondary',
            onClick: handleDeleteOrder,
          },
        ]
      : []),
  ];

  const primaryAction = {
    text: intl.formatMessage(translations.create.button),
    FABVariant: 'extended',
    actions: [
      {
        text: intl.formatMessage(translations.actions.createOrder),
        action: () => history.push('/orders/new'),
        icon: ['fal', 'receipt'],
      },
      {
        text: intl.formatMessage(translations.actions.createPurchaseOrder),
        action: () => history.push('/orders/new?type=purchase'),
        icon: ['fal', 'receipt'],
      },
    ],
  };

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  return (
    <Grid container wrap="nowrap">
      <Page
        title={intl.formatMessage(translations.title)}
        primaryAction={hasPermissions(['odr:orders:write']) ? primaryAction : null}
        style={{ flexGrow: 1 }}
        fullWidth
      >
        <OrdersGrid onActiveItem={handleActiveItem} />
      </Page>
      <DataGridPreview
        title={order?.number ?? 'Concept'}
        open={activeItem !== null}
        onClose={() => setActiveItem(null)}
        menu={previewMenu}
      >
        <LoadingWrapper isLoading={isFetchingOrder} style={{ minHeight: 200 }}>
          {order && <OrderPreview order={order} />}
        </LoadingWrapper>
      </DataGridPreview>
    </Grid>
  );
};

export default Orders;
